import type { ComponentProps } from "react";
import type { Exercise, Plan, Program, UnitsPreference } from "@heraion/shared";

import type { UiTheme } from "../theme";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { LibraryItemCard } from "./LibraryItemCard";
import {
  isReadOnlyItem,
  libraryFavoriteKey,
  personalCreatedBy,
  type LibraryItem,
  type LibraryState,
} from "./useLibrary";

// The <LibraryItemCard> prop bag, derived per row. Split out of
// LibraryBrowser's renderItem to keep that file under the 500-line cap.
// Pure derivation: same props, same guards, same order — the grid-cell
// wrapper and the `key` stay in LibraryBrowser, which owns layout.
//
// ⚠️ Almost every callback here is OPTIONAL on LibraryItemCard and is
// computed by a ternary that can yield undefined. Dropping or loosening one
// of those guards still compiles and silently stops rendering that action,
// so the guards below are copied verbatim rather than rewritten.
export function buildLibraryItemCardProps(ctx: {
  item: LibraryItem;
  theme: UiTheme;
  dark: boolean;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshotFallback?: string) => string;
  unit: UnitsPreference;
  /** Read seam for the coach-badge's name lookup (GET /api/profiles/:sub) —
   *  same host client every other cross-user profile read already uses. */
  apiFetch: ProgramApiFetch;
  /** The signed-in caller's sub — a personal row whose createdBy differs
   *  is one someone else assigned; the badge names them via apiFetch.
   *  `null` means "not yet known" and must never match a `createdBy`
   *  (see `assignedByCoachSub` below — the CRITICAL PE-48 fix). */
  callerSub: string | null;
  /** PE-48 review fix #7 — LibraryBrowser's single useAssignedByCoachNames
   *  batch, keyed by coach sub. Looked up per item below to fill
   *  `assignedByCoachName`, so no card ever runs its own name fetch. */
  coachNames: Record<string, string>;
  /** CO-21 — the active view (Experience), same "not yet known ⇒ null"
   *  convention as callerSub/lib.authoredAs (useLibrary.ts). Gates onAssign
   *  below: assigning from a card is a COACH gesture only — the athlete's
   *  own share flow still lives inside the composer alone. */
  authoredAs?: Program["authoredAs"] | null;
  // Narrowed to the five members actually read, mirroring the sibling
  // useLibraryActions (which takes Pick<LibraryState, ...>) rather than the
  // whole hook surface.
  lib: Pick<
    LibraryState,
    | "cloningKey"
    | "alreadyCloned"
    | "favoriteKeys"
    | "favoritePendingKey"
    | "cloneItem"
    // The follow set. The read-only gate below reads nothing else.
    | "followedKeys"
  >;
  followingKey: string | null;
  mutatingKey: string | null;
  startingPlanId?: string | null;
  onFollow: (item: LibraryItem) => void | Promise<void>;
  onStar: (item: LibraryItem) => void | Promise<void>;
  onSetStatus: (
    item: LibraryItem,
    status: Program["status"],
  ) => void | Promise<void>;
  onStartPlan?: (plan: Plan) => void;
  onEditProgram?: (program: Program) => void;
  onEditPlan?: (plan: Plan) => void;
  /** CO-21 — opens the standalone AssignSheet on this item. Absent ⇒ no
   *  assign action anywhere, same "no seam, no button" degrade as every
   *  other optional handler here (the guard below still applies when
   *  present: coach view + a truly personal, not-assigned-by-someone-else
   *  row). */
  onAssign?: (item: LibraryItem) => void;
  requestDelete: (item: LibraryItem) => void;
  confirmDelete?: (item: LibraryItem, run: () => void) => void;
}): ComponentProps<typeof LibraryItemCard> {
  const {
    item,
    theme,
    dark,
    catalog,
    resolveName,
    unit,
    apiFetch,
    callerSub,
    coachNames,
    authoredAs,
    lib,
    followingKey,
    mutatingKey,
    startingPlanId,
    onFollow,
    onStar,
    onSetStatus,
    onStartPlan,
    onEditProgram,
    onEditPlan,
    onAssign,
    requestDelete,
    confirmDelete,
  } = ctx;
  const favKey = libraryFavoriteKey(item);
  const personal = item.source === "personal";
  const personalProgram = personal && item.kind === "program";
  const personalPlan = personal && item.kind === "plan";
  // Someone else's row in MY partition (the assignment fan-out put it
  // there) — not the tab's job, the item's own authorship. Shown wherever
  // the card renders, "coach" tab or not.
  const createdBy = personalCreatedBy(item);
  // `callerSub === null` ⇒ our own identity isn't known yet ⇒ never claim
  // this row was assigned by someone else (the CRITICAL PE-48 review fix:
  // with `callerSub` defaulting to `""`, `createdBy !== callerSub` was
  // ALWAYS true, so every personal row — including the caller's own —
  // rendered "assigned by <me>").
  const assignedByCoachSub =
    callerSub !== null && createdBy !== null && createdBy !== callerSub
      ? createdBy
      : null;
  // ⭐ The read-only gate (next/coaching.md, "La SPEC del follower" §5). A
  // followed row is a pointer to a program that lives in its author's
  // partition: every write below would 404 there, so the card must not offer
  // it. Three gestures go off (edit, the status moves, assign), one changes
  // meaning (delete → stop following), one stays and IS the way out (clone,
  // which mints a fresh id and stamps the CLONER, clonePayloads.ts).
  // Empty follow set ⇒ `false` for every item ⇒ this whole block is inert.
  const readOnly = isReadOnlyItem(item, lib.followedKeys);
  return {
    theme,
    dark,
    item,
    catalog,
    resolveName,
    unit,
    apiFetch,
    assignedByCoachSub,
    assignedByCoachName:
      assignedByCoachSub !== null
        ? coachNames[assignedByCoachSub] ?? null
        : null,
    cloning: lib.cloningKey === item.key,
    cloneBusy: lib.cloningKey !== null && lib.cloningKey !== item.key,
    alreadyCloned: lib.alreadyCloned(item),
    following: followingKey === item.key,
    favorited: favKey !== null && lib.favoriteKeys.has(favKey),
    favoriteBusy: favKey !== null && lib.favoritePendingKey === favKey,
    onClone: (i) => void lib.cloneItem(i),
    readOnly,
    onFollow:
      !readOnly && personalProgram && item.program.status === "archived"
        ? () => void onFollow(item)
        : undefined,
    onToggleFavorite: favKey !== null ? () => void onStar(item) : undefined,
    onStart:
      personalPlan && onStartPlan && item.plan.status !== "completed"
        ? () => onStartPlan(item.plan)
        : undefined,
    starting: personalPlan && startingPlanId === item.plan.id,
    // The three status moves below write the program row itself, and on a
    // followed row the status is the AUTHOR's: the follower neither
    // publishes nor pauses what he follows (coaching.md §8, decision 5).
    onPublish:
      !readOnly && personalProgram && item.program.status === "draft"
        ? () => void onSetStatus(item, "archived")
        : undefined,
    onUnfollow:
      !readOnly && personalProgram && item.program.status === "active"
        ? () => void onSetStatus(item, "archived")
        : undefined,
    statusBusy: mutatingKey === item.key,
    onEdit:
      readOnly
        ? undefined
        : personalProgram && onEditProgram
          ? () => onEditProgram(item.program)
          : personalPlan && onEditPlan
            ? () => onEditPlan(item.plan)
            : undefined,
    // CO-21 — the smoke gap this ships to close: assigning was ONLY
    // possible inside the editor. The guard is the same shape as onEdit's
    // (personal rows only) plus two more: `assignedByCoachSub === null`
    // (this row isn't already someone ELSE's assignment sitting in my
    // partition — the discriminant computed above) and `authoredAs ===
    // "coach"` (assigning is the coach gesture; the athlete's own
    // Condividi flow stays composer-only, matching CO-20's authoredAs split
    // on ComposerAssigneesBand itself).
    // `!readOnly` on top of `assignedByCoachSub === null`: that one already
    // excludes a followed row, but only once `callerSub` has resolved. The
    // follow set does not wait for it.
    onAssign:
      !readOnly &&
      (personalProgram || personalPlan) &&
      assignedByCoachSub === null &&
      authoredAs === "coach" &&
      onAssign
        ? () => onAssign(item)
        : undefined,
    // On a read-only row this slot is "stop following", and it is always
    // there: it is the follower's exit, and it deletes his own pointer, not
    // the author's program. Otherwise unchanged: followed programs are
    // unfollow-only (the ProgramsPlanView contract), so delete only from
    // draft/published, and plans.
    onDelete:
      readOnly ||
      personalPlan ||
      (personalProgram && item.program.status !== "active")
        ? () => requestDelete(item)
        : undefined,
    confirmDelete: confirmDelete ? "host" : "inline",
  };
}
